import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { FareCode } from "@silversea-ssc/web-fares-fp"
import { AirParams } from "@ssc-booking/api-contracts"
import { buildCruiseName } from "../../../_common/format/cruise"
import { SuiteWithPriceAndConfiguration } from "../read-models/suite-category"
import { ReceiptState, useReceiptReadModel } from "../machines/receipt-machine"
import { fareCardByKey } from "../read-models/fare"
import { useECommerceMachine } from "../../../_common/machines/machines-context"
import { ECommerceState } from "../machines/ecommerce-machine/e-commerce-machine"
import {
    maybeSelectedSuiteCategory,
    SuiteCategoriesState,
    useSuiteCategoriesReadModel,
} from "../machines/suite-categories-machine"
import { SanitySuite } from "../../../_common/sanity/sanity-types"
import { Geolocation } from "../../../_common/providers/geolocation-provider"
import { NotificationContext } from "../../../_common/machines/notification-machine"
import { isServer } from "../../../_common/rendering"
import { TrackingStep } from "../../../_common/tracking/tracking-step"
import { getAAMarket, isSSR } from "./index"

export type PageCat1 = "ecommerce" | "price-configurator"

type ErrorDataLayer = {
    data: _PageDataLayer
    error: NotificationContext
}

interface _PageDataLayer {
    version: number
    user_country?: string
    user_language?: string
    user_geo_adwords?: string
    pageName: string
    page_cat1: PageCat1
    page_cat2: string
    voyage_details: {
        itinerary_details?: string
        code: string
        name: string
        destination_name?: string
        days_duration?: number
        departure_port?: string
        departure_date?: string
        ship_name?: string
        suite?: string
        adults_number: number
        kids_number?: number
        flight_option?: string
        selected_fare?: FareCode
        lead_value?: number
        lead_value_currency?: string
        conversion_step: string
        step_name: string
        is_automatic_selection: boolean
    }
    track_current_page?: string
    is_transcoded_version: boolean
}

interface _404DataLayer {
    version: number
    user_country?: string
    user_language?: string
    user_geo_adwords?: string
    pageName: string
    page_cat1: "404"
    page_cat2: "404"
    track_current_page?: string
    is_transcoded_version: boolean
}

const getCurrentPagePathWithDomain = () => {
    if (typeof window !== "undefined") {
        return window.location.href.split("?")[0]
    }

    return undefined
}

export function buildErrorDataLayer(
    error: NotificationContext,
    state: ECommerceState,
    stateSCRM: SuiteCategoriesState,
    stateRP: ReceiptState,
    step: TrackingStep,
): ErrorDataLayer {
    return {
        data: buildPageDataLayer(state, stateSCRM, stateRP, step),
        error: error,
    }
}

export function buildPageDataLayer(
    state: ECommerceState,
    stateSCRM: SuiteCategoriesState,
    stateRP: ReceiptState,
    step: TrackingStep,
): _PageDataLayer {
    const {
        locale,
        guests: { adults, kids },
        geoLocation,
        flowVersion,
        isAutomaticSelection,
    } = state.context
    const cruiseContent = state.context.sanityContent

    const { cruise, cruiseData } = cruiseContent
    const cruiseName = buildCruiseName(cruiseData.departurePort.name, cruiseData.arrivalPort.name)
    const suiteInfo = maybeSelectedSuiteCategory(stateSCRM)
    const fareCard = fareCardByKey(state.context, state.context.selectedFare?.key)

    const suite = F.pipe(
        suiteInfo,
        O.foldW<undefined, SuiteWithPriceAndConfiguration<SanitySuite>, string>(
            F.constUndefined,
            (x) => x.content.card.title,
        ),
    )

    const totalPrice = stateRP.context.receipt?.price.total
    const lead_value_currency = totalPrice?.currency || geoLocation.defaultCurrency
    const lead_value = totalPrice?.value
    const flight_option = fareCard ? AirParams.asRequestAirFare(fareCard.air) : undefined
    const selected_fare = fareCard?.fare
    const destination_name = cruiseContent.cruise.destination.trackingName

    return {
        version: flowVersion.tracking.version,
        //AUID: getVisitorAuid(),
        page_cat1: flowVersion.tracking.pageCat1,
        page_cat2: step.pageCat2,

        user_country: geoLocation.countryCode,
        user_language: locale,
        user_geo_adwords: getAAMarket(geoLocation.market, geoLocation.countryCode),
        pageName: "booking",
        voyage_details: {
            code: cruise.cruiseCode,
            name: cruiseName,
            destination_name,
            departure_date: cruiseData.departureDate,
            departure_port: cruiseData?.departurePort?.data?.code,
            days_duration: cruiseData.days,
            ship_name: cruiseData.ship?.name,
            suite,
            adults_number: adults,
            kids_number: kids,
            selected_fare,
            flight_option,
            lead_value_currency,
            lead_value,
            conversion_step: step.number,
            step_name: step.name,
            is_automatic_selection: isAutomaticSelection,
        },

        track_current_page: getCurrentPagePathWithDomain(),
        is_transcoded_version: isTranscodedVersion(),
    }
}

export function build404DataLayer(locale: string, geoLocation: Geolocation): _404DataLayer {
    return {
        //NOTE: keep in sync with the one we use on the backend (tracking schema version)
        version: 2,
        page_cat1: "404",
        page_cat2: "404",
        user_country: geoLocation.countryCode,
        user_language: locale,
        user_geo_adwords: getAAMarket(geoLocation.market, geoLocation.countryCode),
        pageName: "booking",
        track_current_page: getCurrentPagePathWithDomain(),
        is_transcoded_version: isTranscodedVersion(),
    }
}

export const useBuildPageDataLayer = (step: TrackingStep | undefined): _PageDataLayer | undefined => {
    const [state] = useECommerceMachine()
    const [stateSCRM] = useSuiteCategoriesReadModel(state)
    const [stateRP] = useReceiptReadModel(state)

    return step ? buildPageDataLayer(state, stateSCRM, stateRP, step) : undefined
}

export type PageDataLayer = _PageDataLayer | _404DataLayer

export function pushToDataLayer(values: PageDataLayer): void {
    if (!isSSR()) {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(values)

        window.wspr?.P("pageload_raw", values)
    }
}

const isTranscodedVersion = () => {
    if (isServer()) return false

    const metaAuthor = window.document.querySelector<HTMLMetaElement>(`meta[name="author"]`)?.getAttribute("content")
    if (!metaAuthor) return false

    return metaAuthor.toLowerCase() == "brandingbrand"
}
